import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, ArrowRight, ArrowLeft, Package, Zap, Tag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISCOUNT_PERCENT = 40;

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOfferActive, setIsOfferActive] = useState(false);

  useEffect(() => {
    fetchCartItems();
    checkOfferStatus();
    
    window.addEventListener('cartUpdated', fetchCartItems);
    return () => window.removeEventListener('cartUpdated', fetchCartItems);
  }, []);

  const checkOfferStatus = () => {
    const offerEndDate = localStorage.getItem('offer_end_date');
    if (offerEndDate) {
      const endDate = new Date(offerEndDate);
      const now = new Date();
      setIsOfferActive(now < endDate);
    }
  };

  const getSessionId = () => {
    return localStorage.getItem('cart_session_id') || '';
  };

  const fetchCartItems = async () => {
    const sessionId = getSessionId();
    if (!sessionId) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCartItems(response.data);
      localStorage.setItem('cart_count', response.data.length.toString());
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`);
      toast.success('Item removed from cart');
      fetchCartItems();
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item');
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + item.price, 0);
  };

  const calculateDiscount = () => {
    if (!isOfferActive) return 0;
    return calculateSubtotal() * (DISCOUNT_PERCENT / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const getDiscountedPrice = (price) => {
    if (!isOfferActive) return price;
    return price * (1 - DISCOUNT_PERCENT / 100);
  };

  const proceedToCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-dark)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
          <div className="text-[var(--text-muted)]">Loading cart...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      <section className="pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-[7.6923%]">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#3B82F6] hover:text-[var(--text-primary)] transition-colors duration-300 mb-6"
            >
              <ArrowLeft size={20} />
              <span>Continue Shopping</span>
            </button>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center">
                <ShoppingCart size={28} className="text-white" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] leading-tight" style={{ letterSpacing: '-0.02em' }}>
                  Shopping Cart
                </h1>
                <p className="text-[var(--text-muted)] mt-1">
                  {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
                </p>
              </div>
            </div>
          </div>

          {/* Discount Banner */}
          {isOfferActive && cartItems.length > 0 && (
            <div className="mb-8 p-4 bg-gradient-to-r from-[#F97316]/20 via-[#EC4899]/20 to-[#8B5CF6]/20 border border-[#EC4899]/30 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#F97316] to-[#EC4899] flex items-center justify-center">
                  <Zap size={20} className="text-white" fill="white" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">🎉 {DISCOUNT_PERCENT}% OFF Applied!</p>
                  <p className="text-white/70 text-sm">Limited time offer - Discount automatically applied at checkout</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-[#10B981] font-bold text-xl">-${calculateDiscount().toLocaleString()}</p>
                  <p className="text-white/50 text-xs">You're saving</p>
                </div>
              </div>
            </div>
          )}

          {cartItems.length === 0 ? (
            /* Empty Cart */
            <div className="text-center py-16 bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl">
              <div className="w-24 h-24 rounded-full bg-[var(--bg-dark)] flex items-center justify-center mx-auto mb-6">
                <ShoppingCart size={48} className="text-[var(--text-muted)]" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Your cart is empty</h2>
              <p className="text-[var(--text-muted)] mb-8 max-w-md mx-auto">Explore our services and add packages to your cart to get started.</p>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl inline-flex items-center gap-3"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              >
                Browse Services
                <ArrowRight size={20} />
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 flex items-center justify-between gap-6 hover:border-[var(--border-hover)] transition-all duration-300"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-[#3B82F6]/20 to-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                        <Package size={24} className="text-[#8B5CF6]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[var(--text-primary)] mb-1">
                          {item.service_name}
                        </h3>
                        <p className="text-[#8B5CF6] font-medium text-sm mb-1">
                          {item.package_name} Package
                        </p>
                        <p className="text-[var(--text-muted)] text-xs">
                          One-time payment
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      {isOfferActive ? (
                        <div className="mb-3">
                          <div className="flex items-center gap-2 justify-end">
                            <span className="text-[var(--text-muted)] line-through text-lg">
                              ${item.price.toLocaleString()}
                            </span>
                            <span className="px-2 py-0.5 bg-[#10B981]/20 text-[#10B981] text-xs font-bold rounded">
                              -{DISCOUNT_PERCENT}%
                            </span>
                          </div>
                          <div className="text-2xl font-bold text-[#10B981]">
                            ${getDiscountedPrice(item.price).toLocaleString()}
                          </div>
                        </div>
                      ) : (
                        <div className="text-2xl font-bold text-[var(--text-primary)] mb-3">
                          ${item.price.toLocaleString()}
                        </div>
                      )}
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-400 hover:text-red-300 transition-colors duration-300 flex items-center gap-2 text-sm"
                      >
                        <Trash2 size={16} />
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-8 sticky top-24">
                  <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-6">Order Summary</h2>

                  <div className="space-y-4 mb-6 pb-6 border-b border-[var(--border)]">
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>Subtotal</span>
                      <span>${calculateSubtotal().toLocaleString()}</span>
                    </div>
                    
                    {isOfferActive && (
                      <div className="flex justify-between">
                        <span className="flex items-center gap-2 text-[#10B981]">
                          <Tag size={16} />
                          {DISCOUNT_PERCENT}% Discount
                        </span>
                        <span className="text-[#10B981] font-semibold">-${calculateDiscount().toLocaleString()}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>Tax</span>
                      <span className="text-[var(--text-muted)]">Calculated at checkout</span>
                    </div>
                  </div>

                  <div className="flex justify-between text-xl font-bold mb-8">
                    <span className="text-[var(--text-primary)]">Total</span>
                    <div className="text-right">
                      {isOfferActive && (
                        <div className="text-[var(--text-muted)] line-through text-sm font-normal">
                          ${calculateSubtotal().toLocaleString()}
                        </div>
                      )}
                      <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                        ${calculateTotal().toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={proceedToCheckout}
                    className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-[1.02] hover:shadow-xl flex items-center justify-center gap-3"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                  >
                    Proceed to Checkout
                    <ArrowRight size={20} />
                  </button>

                  {isOfferActive && (
                    <div className="mt-4 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl text-center">
                      <p className="text-[#10B981] text-sm font-medium">
                        🎉 You're saving ${calculateDiscount().toLocaleString()} with the limited time offer!
                      </p>
                    </div>
                  )}

                  <p className="text-[var(--text-muted)] text-sm text-center mt-4">
                    Secure payment powered by PayPal
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Chatbot proactiveDelay={30000} />
    </div>
  );
};

export default Cart;
